/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import * as pdfFillerApi from '@/api/pdfFillerApi'
import useIntegration from '@/hooks/useIntegration'
import usePermissions from '@/hooks/usePermissions'
import { PDFFillerContext } from '@/pages/Contexts/PdfFillerContext'
import Button from '@/ui/atoms/Button'
import Heading from '@/ui/atoms/Heading'
import Space from '@/ui/atoms/Space'
import TextField from '@/ui/molecules/TextField'
import ToggleField from '@/ui/molecules/ToggleField'
import Select from '@/ui/Select'
import { ZentakeContext } from '@/utils/ZentakeContext'

function SettingsPanel() {
  const history = useHistory()
  const [isDeleting, setIsDeleting] = React.useState(false)
  const { integrationReports, integration, integrationTitledName } =
    useIntegration()
  const [integrationReportsSearch, setIntegrationReportsSearch] =
    React.useState('')
  const { teamDetails } = React.useContext(ZentakeContext)
  const auto_sign_off_enabled =
    !!teamDetails?.integration?.auto_sign_off_enabled

  const integrationReportsOptions = React.useMemo(() => {
    return integrationReports
      .filter(
        (report) =>
          integrationReportsSearch === '' ||
          report.label
            .toLowerCase()
            .includes(integrationReportsSearch.toLowerCase())
      )
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((report) => ({
        value: report.label,
        label: report.label,
      }))
  }, [integrationReports, integrationReportsSearch])

  const { canDeletePacketForm, canEditPacketForm } = usePermissions()

  const {
    setSettingsOpen,
    selectedInputFieldId,
    setSelectedInputFieldId,
    settingsOpen,
    documentName,
    setDocumentName,
    pdfReportType,
    setPdfReportType,
    resourceId,
    autoSignOffEnabled,
    setAutoSignOffEnabled,
    elationPdfNonFax,
    setElationPdfNonFax,
  } = React.useContext(PDFFillerContext)

  React.useEffect(() => {
    if (selectedInputFieldId) {
      setSettingsOpen(false)
    }
  }, [selectedInputFieldId, setSettingsOpen])

  React.useEffect(() => {
    if (settingsOpen) {
      setSelectedInputFieldId(null)
    }
  }, [settingsOpen, setSelectedInputFieldId])

  const handleDelete = React.useCallback(async () => {
    const confirmation = window.confirm('Are you sure you want to delete?')

    if (!confirmation) return

    setIsDeleting(true)

    return pdfFillerApi
      .deletePdfDocument(resourceId)
      .then(() => {
        toast.success('Document deleted')
        history.push('/forms')
      })
      .catch(() => {
        toast.error('Error deleting document')
      })
      .finally(() => {
        setIsDeleting(false)
      })
  }, [history, resourceId])

  const has_integration = !!integration && integrationReports.length > 0

  React.useEffect(() => {
    if (selectedInputFieldId) {
      setSettingsOpen(false)
    }
  }, [selectedInputFieldId, setSettingsOpen])

  React.useEffect(() => {
    if (settingsOpen) {
      setSelectedInputFieldId(null)
    }
  }, [settingsOpen, setSelectedInputFieldId])

  const removeIntegration = React.useCallback(() => {
    setPdfReportType(null)
  }, [setPdfReportType])

  return (
    <Space size="xlarge">
      <Space size="large">
        <Heading>Document Settings</Heading>
        <TextField
          label="Document Name"
          onChange={(value) => setDocumentName(value as string)}
          value={documentName}
        />
        {has_integration && (
          <>
            <div className="mb-3 mt-6 border-b pb-1">
              <button
                className={`relative top-1 float-right text-sm ${
                  !pdfReportType ? 'text-red-200' : 'text-red-500'
                }`}
                disabled={!pdfReportType}
                onClick={removeIntegration}
              >
                Clear
              </button>
              <p className="font-medium text-txt-emphasis">
                Integration - {integrationTitledName}
              </p>
            </div>
            <div>
              {/* @ts-ignore */}
              <Select
                grayOutWhenDisabled
                showOptions
                disabled={!canEditPacketForm || elationPdfNonFax}
                label="PDF Integration"
                onChange={(option: { value: string }) => {
                  setPdfReportType(option?.value || null)
                }}
                onType={setIntegrationReportsSearch}
                options={integrationReportsOptions}
                placeholder="Select a report type"
                value={integrationReportsOptions.find(
                  (option) => option.value === pdfReportType
                )}
                willShowNotAllowedCursor={
                  !canEditPacketForm || elationPdfNonFax
                }
              />
            </div>
            <ToggleField
              compact
              grayOutWhenDisabled
              horizontal
              lightLabel
              primary
              disabled={!auto_sign_off_enabled}
              label="Auto Sign Off"
              onChange={() => {
                setAutoSignOffEnabled(!autoSignOffEnabled)
              }}
              value={autoSignOffEnabled}
            />
            <ToggleField
              compact
              grayOutWhenDisabled
              horizontal
              lightLabel
              primary
              label="Send to Unfiled (non-fax)"
              onChange={() => {
                setElationPdfNonFax(!elationPdfNonFax)
                removeIntegration()
              }}
              value={elationPdfNonFax}
            />
          </>
        )}
      </Space>
      {canDeletePacketForm && (
        <Button $block $danger loading={isDeleting} onClick={handleDelete}>
          Delete Document
        </Button>
      )}
    </Space>
  )
}

export default SettingsPanel
